import { type AuthUser } from '@/domain/account/types';
import { type RoadMap, type RoadMapUpdate } from '@/domain/roadmap/types';

import { isUserAdmin } from './permissions';
import {
  type GenericProduct,
  isProductManager,
  isProductOwner,
  isQaSpecialist,
  isTechnologyManager,
} from './productPermission';

interface GenericRoadmap {
  item: string;
  idRoadmap: number;
  idOwner?: number;
  idReporter?: number;
  createdById?: number;
  isParticipant?: boolean;
  responsibleIds?: number[];
  locked: boolean;
}

export interface GenericRoadmapWithProduct extends GenericRoadmap {
  product: GenericProduct | null;
}

export const isRoadmapReporter = (user: AuthUser | undefined, roadmap: GenericRoadmap): boolean =>
  roadmap.idReporter === user?.idUser;

export const isRoadmapCreator = (user: AuthUser | undefined, roadmap: GenericRoadmap): boolean =>
  roadmap.createdById === user?.idUser;

export const isRoadmapParticipant = (
  user: AuthUser | undefined,
  roadmap: Pick<RoadMapUpdate, 'participants'>,
): boolean => roadmap.participants?.some((p) => p.idUser === user?.idUser) ?? false;

export const isRoadmapOwner = (user: AuthUser | undefined, roadmap: GenericRoadmap): boolean =>
  roadmap.idOwner === user?.idUser;

export const isRoadmapResponsible = (user: AuthUser | undefined, roadmap: GenericRoadmap): boolean => {
  if (user?.idUser === undefined) return false;
  return roadmap.responsibleIds?.includes(user.idUser) ?? false;
};

export const canEditRoadmap = (user: AuthUser | undefined, roadmap: GenericRoadmapWithProduct): boolean => {
  if (user == null) {
    return false;
  }
  return (
    isUserAdmin(user) ||
    isRoadmapOwner(user, roadmap) ||
    isProductOwner(user, roadmap.product) ||
    isTechnologyManager(user, roadmap.product) ||
    isQaSpecialist(user, roadmap.product) ||
    isRoadmapCreator(user, roadmap) ||
    isProductManager(user, roadmap.product) ||
    isRoadmapResponsible(user, roadmap)
  );
};

export const canEditRoadmapDetails = (user: AuthUser | undefined, roadmap: RoadMap | RoadMapUpdate): boolean => {
  if (user == null) {
    return false;
  }
  return (
    isUserAdmin(user) ||
    isRoadmapCreator(user, roadmap) ||
    isProductOwner(user, roadmap.product) ||
    isTechnologyManager(user, roadmap.product) ||
    isProductManager(user, roadmap.product) ||
    isRoadmapResponsible(user, roadmap)
  );
};

export const canDeleteRoadmap = (user: AuthUser | undefined, roadmap: GenericRoadmapWithProduct): boolean => {
  if (user == null) {
    return false;
  }
  return (
    isUserAdmin(user) ||
    isProductOwner(user, roadmap.product) ||
    isTechnologyManager(user, roadmap.product) ||
    isRoadmapCreator(user, roadmap) ||
    isProductManager(user, roadmap.product)
  );
};

export const canEditRoadmapTempo = (user: AuthUser | undefined, roadmap: GenericRoadmapWithProduct): boolean => {
  if (!user) return false;

  return (
    isUserAdmin(user) ||
    isRoadmapOwner(user, roadmap) ||
    isProductOwner(user, roadmap.product) ||
    isProductManager(user, roadmap.product) ||
    isRoadmapResponsible(user, roadmap)
  );
};
